/* global WebSocket */ import { matchFilters } from "./filter.js";
import { getHex64, getSubscriptionId } from "./fakejson.js";
import { normalizeURL } from "./utils.js";
import { makeAuthEvent } from "./nip42.js";
export class SendingOnClosedConnection extends Error {
  constructor(message, relay){
    super(`Tried to send message '${message} on a closed connection to ${relay}.`);
    this.name = 'SendingOnClosedConnection';
  }
}
export class AbstractRelay {
  url;
  _connected = false;
  onclose = null;
  onnotice = (msg)=>console.debug(`NOTICE from ${this.url}: ${msg}`);
  onauth;
  baseEoseTimeout = 4400;
  publishTimeout = 4400;
  pingFrequency = 29000;
  pingTimeout = 20000;
  resubscribeBackoff = [
    10000,
    10000,
    10000,
    20000,
    20000,
    30000,
    60000
  ];
  openSubs = new Map();
  enablePing;
  enableReconnect;
  idleSince = Date.now() // when undefined that means it isn't idle
  ;
  ongoingOperations = 0 // used to compute idleness
  ;
  reconnectTimeoutHandle;
  pingIntervalHandle;
  reconnectAttempts = 0;
  skipReconnection = false;
  connectionPromise;
  openCountRequests = new Map();
  openEventPublishes = new Map();
  ws;
  challenge;
  authPromise;
  serial = 0;
  verifyEvent;
  _WebSocket;
  constructor(url, opts){
    this.url = normalizeURL(url);
    this.verifyEvent = opts.verifyEvent;
    this._WebSocket = opts.websocketImplementation || WebSocket;
    this.enablePing = opts.enablePing;
    this.enableReconnect = opts.enableReconnect || false;
  }
  static async connect(url, opts) {
    const relay = new AbstractRelay(url, opts);
    await relay.connect(opts);
    return relay;
  }
  closeAllSubscriptions(reason) {
    for (let [_, sub] of this.openSubs){
      sub.close(reason);
    }
    this.openSubs.clear();
    for (let [_, ep] of this.openEventPublishes){
      ep.reject(new Error(reason));
    }
    this.openEventPublishes.clear();
    for (let [_, cr] of this.openCountRequests){
      cr.reject(new Error(reason));
    }
    this.openCountRequests.clear();
  }
  get connected() {
    return this._connected;
  }
  async reconnect() {
    const backoff = this.resubscribeBackoff[Math.min(this.reconnectAttempts, this.resubscribeBackoff.length - 1)];
    this.reconnectAttempts++;
    this.reconnectTimeoutHandle = setTimeout(async ()=>{
      try {
        await this.connect();
      } catch (err) {
      // this will be called again through onclose/onerror
      }
    }, backoff);
  }
  handleHardClose(reason) {
    if (this.pingIntervalHandle) {
      clearInterval(this.pingIntervalHandle);
      this.pingIntervalHandle = undefined;
    }
    this._connected = false;
    this.connectionPromise = undefined;
    this.idleSince = undefined;
    if (this.enableReconnect && !this.skipReconnection) {
      this.reconnect();
    } else {
      this.onclose?.();
      this.closeAllSubscriptions(reason);
    }
  }
  async connect(opts) {
    let connectionTimeoutHandle;
    if (this.connectionPromise) return this.connectionPromise;
    this.challenge = undefined;
    this.authPromise = undefined;
    this.skipReconnection = false;
    this.connectionPromise = new Promise((resolve, reject)=>{
      if (opts?.timeout) {
        connectionTimeoutHandle = setTimeout(()=>{
          reject('connection timed out');
          this.connectionPromise = undefined;
          this.skipReconnection = true;
          this.onclose?.();
          this.handleHardClose('relay connection timed out');
        }, opts.timeout);
      }
      if (opts?.abort) {
        opts.abort.onabort = reject;
      }
      try {
        this.ws = new this._WebSocket(this.url);
      } catch (err) {
        clearTimeout(connectionTimeoutHandle);
        reject(err);
        return;
      }
      this.ws.onopen = ()=>{
        if (this.reconnectTimeoutHandle) {
          clearTimeout(this.reconnectTimeoutHandle);
          this.reconnectTimeoutHandle = undefined;
        }
        clearTimeout(connectionTimeoutHandle);
        this._connected = true;
        const isReconnection = this.reconnectAttempts > 0;
        this.reconnectAttempts = 0;
        // resubscribe to all open subscriptions
        for (const sub of this.openSubs.values()){
          sub.eosed = false;
          if (isReconnection) {
            for(let f = 0; f < sub.filters.length; f++){
              if (sub.lastEmitted) {
                sub.filters[f].since = sub.lastEmitted + 1;
              }
            }
          }
          sub.fire();
        }
        if (this.enablePing) {
          this.pingIntervalHandle = setInterval(()=>this.pingpong(), this.pingFrequency);
        }
        resolve();
      };
      this.ws.onerror = ()=>{
        clearTimeout(connectionTimeoutHandle);
        reject('connection failed');
        this.connectionPromise = undefined;
        this.skipReconnection = true;
        this.onclose?.();
        this.handleHardClose('relay connection failed');
      };
      this.ws.onclose = (ev)=>{
        clearTimeout(connectionTimeoutHandle);
        reject(ev.message || 'websocket closed');
        this.handleHardClose('relay connection closed');
      };
      this.ws.onmessage = this._onmessage.bind(this);
    });
    return this.connectionPromise;
  }
  waitForPingPong() {
    return new Promise((resolve)=>{
      // listen for pong
      ;
      this.ws.once('pong', ()=>resolve(true));
      // send a ping
      this.ws.ping();
    });
  }
  waitForDummyReq() {
    return new Promise((resolve, reject)=>{
      if (!this.connectionPromise) return reject(new Error(`no connection to ${this.url}, can't ping`));
      // make a dummy request with expected empty eose reply
      // ["REQ", "_", {"ids":["aaaa...aaaa"], "limit": 0}]
      try {
        const sub = this.subscribe([
          {
            ids: [
              'aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa'
            ],
            limit: 0
          }
        ], {
          label: '<forced-ping>',
          oneose: ()=>{
            resolve(true);
            sub.close();
          },
          onclose () {
            // if we get a CLOSED it's because the relay is alive
            resolve(true);
          },
          eoseTimeout: this.pingTimeout + 1000
        });
      } catch (err) {
        reject(err);
      }
    });
  }
  // nodejs requires this magic here to ensure connections are closed when internet goes off and stuff
  // in browsers it's done automatically. see https://github.com/nbd-wtf/nostr-tools/issues/491
  async pingpong() {
    // if the websocket is connected
    if (this.ws?.readyState === 1) {
      // wait for either a ping-pong reply or a timeout
      const result = await Promise.any([
        // browsers don't have ping so use a dummy req
        this.ws && this.ws.ping && this.ws.once ? this.waitForPingPong() : this.waitForDummyReq(),
        new Promise((res)=>setTimeout(()=>res(false), this.pingTimeout))
      ]);
      if (!result) {
        // pingpong closing socket
        if (this.ws?.readyState === this._WebSocket.OPEN) {
          this.ws?.close();
        }
      }
    }
  }
  async send(message) {
    if (!this.connectionPromise) throw new SendingOnClosedConnection(message, this.url);
    this.connectionPromise.then(()=>{
      this.ws?.send(message);
    });
  }
  async auth(signAuthEvent) {
    const challenge = this.challenge;
    if (!challenge) throw new Error("can't perform auth, no challenge was received");
    if (this.authPromise) return this.authPromise;
    this.authPromise = new Promise(async (resolve, reject)=>{
      try {
        let evt = await signAuthEvent(makeAuthEvent(this.url, challenge));
        let timeout = setTimeout(()=>{
          let ep = this.openEventPublishes.get(evt.id);
          if (ep) {
            ep.reject(new Error('auth timed out'));
            this.openEventPublishes.delete(evt.id);
          }
        }, this.publishTimeout);
        this.openEventPublishes.set(evt.id, {
          resolve,
          reject,
          timeout
        });
        this.send('["AUTH",' + JSON.stringify(evt) + ']');
      } catch (err) {
        console.warn('subscribe auth function failed:', err);
      }
    });
    return this.authPromise;
  }
  async publish(event) {
    this.idleSince = undefined;
    this.ongoingOperations++;
    const ret = new Promise((resolve, reject)=>{
      const timeout = setTimeout(()=>{
        const ep = this.openEventPublishes.get(event.id);
        if (ep) {
          ep.reject(new Error('publish timed out'));
          this.openEventPublishes.delete(event.id);
        }
      }, this.publishTimeout);
      this.openEventPublishes.set(event.id, {
        resolve,
        reject,
        timeout
      });
    });
    this.send('["EVENT",' + JSON.stringify(event) + ']');
    // compute idleness state
    this.ongoingOperations--;
    if (this.ongoingOperations === 0) this.idleSince = Date.now();
    return ret;
  }
  async count(filters, params) {
    this.serial++;
    const id = params?.id || 'count:' + this.serial;
    const ret = new Promise((resolve, reject)=>{
      this.openCountRequests.set(id, {
        resolve,
        reject
      });
    });
    this.send('["COUNT","' + id + '",' + JSON.stringify(filters).substring(1));
    return ret;
  }
  subscribe(filters, params) {
    if (params.label !== '<forced-ping>') {
      this.idleSince = undefined;
      this.ongoingOperations++;
    }
    const sub = this.prepareSubscription(filters, params);
    sub.fire();
    if (params.abort) {
      params.abort.onabort = ()=>sub.close(String(params.abort.reason || '<aborted>'));
    }
    return sub;
  }
  prepareSubscription(filters, params) {
    this.serial++;
    const id = params.id || (params.label ? params.label + ':' : 'sub:') + this.serial;
    const sub = new Subscription(this, id, filters, params);
    this.openSubs.set(id, sub);
    return sub;
  }
  close() {
    this.skipReconnection = true;
    if (this.reconnectTimeoutHandle) {
      clearTimeout(this.reconnectTimeoutHandle);
      this.reconnectTimeoutHandle = undefined;
    }
    if (this.pingIntervalHandle) {
      clearInterval(this.pingIntervalHandle);
      this.pingIntervalHandle = undefined;
    }
    this.closeAllSubscriptions('relay connection closed by us');
    this._connected = false;
    this.idleSince = undefined;
    this.onclose?.();
    if (this.ws?.readyState === this._WebSocket.OPEN) {
      this.ws?.close();
    }
  }
  // this is the function assigned to this.ws.onmessage
  // it's exposed for testing and debugging purposes
  _onmessage(ev) {
    const json = ev.data;
    if (!json) {
      return;
    }
    // shortcut EVENT sub
    const subid = getSubscriptionId(json);
    if (subid) {
      const so = this.openSubs.get(subid);
      if (!so) {
        // this is an EVENT message, but for a subscription we don't have, so just stop here
        return;
      }
      // this will be called only when this message is a EVENT message for a subscription we have
      // we do this before parsing the JSON to not have to do that for duplicate events
      //   since JSON parsing is slow
      const id = getHex64(json, 'id');
      const alreadyHave = so.alreadyHaveEvent?.(id);
      // notify any interested client that the relay has this event
      // (do this after alreadyHaveEvent() because the client may rely on this to answer that)
      so.receivedEvent?.(this, id);
      if (alreadyHave) {
        // if we had already seen this event we can just stop here
        return;
      }
    }
    try {
      let data = JSON.parse(json);
      // we won't do any checks against the data since all failures (i.e. invalid messages from relays)
      // will naturally be caught by the encompassing try..catch block
      switch(data[0]){
        case 'EVENT':
          {
            const so = this.openSubs.get(data[1]);
            const event = data[2];
            if (this.verifyEvent(event) && matchFilters(so.filters, event)) {
              so.onevent(event);
            }
            if (!so.lastEmitted || so.lastEmitted < event.created_at) so.lastEmitted = event.created_at;
            return;
          }
        case 'COUNT':
          {
            const id = data[1];
            const payload = data[2];
            const cr = this.openCountRequests.get(id);
            if (cr) {
              cr.resolve(payload.count);
              this.openCountRequests.delete(id);
            }
            return;
          }
        case 'EOSE':
          {
            const so = this.openSubs.get(data[1]);
            if (!so) return;
            so.receivedEose();
            return;
          }
        case 'OK':
          {
            const id = data[1];
            const ok = data[2];
            const reason = data[3];
            const ep = this.openEventPublishes.get(id);
            if (ep) {
              clearTimeout(ep.timeout);
              if (ok) ep.resolve(reason);
              else ep.reject(new Error(reason));
              this.openEventPublishes.delete(id);
            }
            return;
          }
        case 'CLOSED':
          {
            const id = data[1];
            const so = this.openSubs.get(id);
            if (!so) return;
            so.closed = true;
            so.close(data[2]);
            return;
          }
        case 'NOTICE':
          {
            this.onnotice(data[1]);
            return;
          }
        case 'AUTH':
          {
            this.challenge = data[1];
            if (this.onauth) {
              this.auth(this.onauth);
            }
            return;
          }
        default:
          {
            const so = this.openSubs.get(data[1]);
            so?.oncustom?.(data);
            return;
          }
      }
    } catch (err) {
      const [_, __, event] = JSON.parse(json);
      window.printer.maybe(event.pubkey, ':: caught err', event, this.url, err);
      return;
    }
  }
}
export class Subscription {
  relay;
  id;
  lastEmitted;
  closed = false;
  eosed = false;
  filters;
  alreadyHaveEvent;
  receivedEvent;
  onevent;
  oneose;
  onclose;
  // will get any messages that have this subscription id as their second item and are not default standard
  oncustom;
  eoseTimeout;
  eoseTimeoutHandle;
  constructor(relay, id, filters, params){
    if (filters.length === 0) throw new Error("subscription can't be created with zero filters");
    this.relay = relay;
    this.filters = filters;
    this.id = id;
    this.alreadyHaveEvent = params.alreadyHaveEvent;
    this.receivedEvent = params.receivedEvent;
    this.eoseTimeout = params.eoseTimeout || relay.baseEoseTimeout;
    this.oneose = params.oneose;
    this.onclose = params.onclose;
    this.onevent = params.onevent || ((event)=>{
      console.warn(`onevent() callback not defined for subscription '${this.id}' in relay ${this.relay.url}. event received:`, event);
    });
  }
  fire() {
    this.relay.send('["REQ","' + this.id + '",' + JSON.stringify(this.filters).substring(1));
    // only now we start counting the eoseTimeout
    this.eoseTimeoutHandle = setTimeout(this.receivedEose.bind(this), this.eoseTimeout);
  }
  receivedEose() {
    if (this.eosed) return;
    clearTimeout(this.eoseTimeoutHandle);
    this.eosed = true;
    this.oneose?.();
  }
  close(reason = 'closed by caller') {
    if (!this.closed && this.relay.connected) {
      // if the connection was closed by the user calling .close() we will send a CLOSE message
      // otherwise this._open will be already set to false so we will skip this
      try {
        this.relay.send('["CLOSE",' + JSON.stringify(this.id) + ']');
      } catch (err) {
        if (err instanceof SendingOnClosedConnection) {
        /* doesn't matter, it's ok */ } else {
          throw err;
        }
      }
      this.closed = true;
    }
    this.relay.openSubs.delete(this.id);
    // compute idleness state
    this.relay.ongoingOperations--;
    if (this.relay.ongoingOperations === 0) this.relay.idleSince = Date.now();
    this.onclose?.(reason);
  }
}
//# sourceMappingURL=abstract-relay.js.map